import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  forgotPassword,
  getUser,
  loginCredentials,
  logout,
  register,
  resetPassword,
  verifyResetPassword,
} from '@/app/auth/api/endpoints.ts';
import { resetAuthStore, updateUser, useAuthStore } from '@/stores/auth.store.ts';
import { useNotifications } from '@/hooks/use-notifications.ts';
import { useLoginLogout } from '@/hooks/use-login-logout.ts';
import { AxiosError } from 'axios';
import { useNavigate, useSearch } from '@tanstack/react-router';

export function useUpdateUserMutation() {
  return useMutation({
    mutationFn: () => getUser(),
    onSuccess: (res) => {
      if (res?.id) {
        updateUser(res);
      } else {
        resetAuthStore(false);
      }
    },
    onError: () => {
      // resetAuthStore(false); // Commented out to prevent issue on reset password screen
    }
  });
}

export function useRegisterMutation() {
  const { mutateProps } = useNotifications();
  return useMutation({
    mutationFn: (data: any) => register(data),
    ...mutateProps,
  });
}

export function useLoginCredentialsMutation() {
  const { showError, showSuccess, mutateProps } = useNotifications();
  const { doLogin } = useLoginLogout();
  const searchParams: any = useSearch({ strict: false });
  const stateRedirectUrl = useAuthStore((state) => state.redirectUrl);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: { email: string; password: string }) => loginCredentials(data),
    ...mutateProps,
    onSuccess: async (res) => {
      if ('user' in res) {
        doLogin(res.user);
        showSuccess('Welcome Back, ' + res.user.name);
        let _redirect: string;
        if (searchParams.redirect) {
          _redirect = searchParams.redirect;
        } else {
          _redirect = stateRedirectUrl || '/';
        }
        await navigate({ to: _redirect });
      } else {
        showError('There was an error');
      }
    },
  });
}

export function useLogoutMutation() {
  const { mutateProps, showSuccess } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    ...mutateProps,
    onSuccess: async () => {
      resetAuthStore();
      queryClient.clear();
      await navigate({ to: '/' });
      showSuccess('Logged out successfully');
    },
    onError: async (err: Error) => {
      if (err instanceof AxiosError) {
        if (err.response?.status === 401) {
          resetAuthStore();
          showSuccess('Logged out successfully');
          await navigate({ to: '/' });
          return;
        }
      }
      console.error('Logout error:', err);
    },
  });
}

export function useForgotPasswordMutation() {
  const { showSuccess, mutateProps } = useNotifications();
  return useMutation({
    mutationFn: (data: { email: string }) => forgotPassword(data),
    ...mutateProps,
    onSuccess: (res) => {
      if (res?.success) {
        showSuccess('Password reset request sent.');
      }
    },
  });
}

export function useVerifyResetPasswordMutation() {
  return useMutation({
    mutationFn: (data: { token: string; email: string }) => verifyResetPassword(data),
  });
}

export function useResetPasswordMutation() {
  const { showSuccess, showError, mutateProps } = useNotifications();
  const { doLogin } = useLoginLogout();
  return useMutation({
    mutationFn: (data: { email: string; token: string; password: string }) => resetPassword(data),
    ...mutateProps,
    onSuccess: async (res) => {
      if (res?.user) {
        doLogin(res.user);
        showSuccess('Password reset successfully');
      } else {
        showError('There was an error resetting your password');
      }
    },
  });
}
